import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Divider, Paper, Typography, useTheme } from "@mui/material";
import * as Sentry from "@sentry/react";
import { CardPreOferta, PageHeader, StepActions } from "@components/ui_v2";
import { getSegmentColor } from "@components/factoraje";
import { formatCurrencyWhole } from "@helpers/funciones";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { usePostValidateNextStageMutation } from "@api/applications";
import { ApiError } from "@interfaces/response";
import { STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";

// TODO: datos fijos del prototipo (`FX_LINEA` y `fxAutorizacion()` con la solicitud demo) mientras no exista el
// servicio de la línea preaprobada. Al conectarlo, solo cambian estas constantes por la respuesta.
const LINEA_PREAPROBADA = 3100000;
const CONDICIONES = { aforo: 90, tasa: 21.6, comision: 1.2, plazoMax: 120 };
const LIMITES_POR_EMPRESA = [
  { nombre: "Refacciones Vega SA", max: 1150000, plazoMax: 120 },
  { nombre: "Chedraui", max: 1150000, plazoMax: 90 },
  { nombre: "Grupo Bimbo", max: 750000, plazoMax: 60 },
  { nombre: "Cadena Comercial OXXO", max: 750000, plazoMax: 90 },
];

// "Cómo se ve una operación": factura de ejemplo, anticipo por el aforo y costo = interés del anticipo por los días
// (año de 360) + comisión sobre la factura.
const EJEMPLO = { factura: 500000, dias: 60 };
const ANTICIPO = (EJEMPLO.factura * CONDICIONES.aforo) / 100;
const COSTO =
  ANTICIPO * (CONDICIONES.tasa / 100) * (EJEMPLO.dias / 360) + EJEMPLO.factura * (CONDICIONES.comision / 100);

/** `.ld-offer-stat-k` / `.ld-offer-stat-v` del diseño, para las cifras de la card de ejemplo. */
const STAT_LABEL_SX = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "tokens.textTertiary",
  mb: "3px",
} as const;
const STAT_VALUE_SX = { fontSize: 19, fontWeight: 600, color: "primary.dark", fontVariantNumeric: "tabular-nums" };

/** `.card-title` / `.card-sub` del diseño. */
const CardTitulo = ({ title, sub }: { title: string; sub: string }) => (
  <>
    <Typography sx={{ fontSize: 17, fontWeight: 600, letterSpacing: "-0.015em", color: "primary.dark", mb: "4px" }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 13.5, color: "tokens.textTertiary", lineHeight: 1.55 }}>{sub}</Typography>
  </>
);

/**
 * Línea de factoraje preaprobada de Flux (`FX_PreAprobacion` del diseño): la línea revolvente con sus condiciones,
 * el límite por empresa y un ejemplo de operación. Por ahora con datos fijos; solo "Aceptar" es funcional.
 */
export const OfertaFlux = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus } = useStatusConfig(STATUS_CATALOG.oferta.code);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // "Aceptar y asignar mi línea" solo avanza al siguiente status de `status_config` y navega a su pantalla.
  const handleAccept = async () => {
    try {
      setIsSubmitting(true);
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }
      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();
      navigate(nextStatus.url);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "OfertaFlux-accept" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Análisis · Línea preaprobada"
        title="Tu línea de factoraje está preaprobada"
        subtitle={
          <>
            Tienes una línea revolvente preautorizada de <strong>{formatCurrencyWhole(LINEA_PREAPROBADA)} MXN</strong>.
            Cada factura que descuentes libera saldo conforme te pagan tus clientes.
          </>
        }
      />

      <CardPreOferta
        eyebrow="Línea revolvente · Factoraje electrónico"
        badge="Preaprobada"
        amount={formatCurrencyWhole(LINEA_PREAPROBADA)}
        amountCaption="MXN disponibles"
        sub="Vigente por 30 días · Sujeta a revisión documental y validación de cada factura"
        items={[
          { label: "Aforo", value: `${CONDICIONES.aforo}%` },
          { label: "Tasa anual", value: `${CONDICIONES.tasa}%` },
          { label: "Comisión por operación", value: `${CONDICIONES.comision}%` },
          { label: "Plazo máximo de factura", value: `${CONDICIONES.plazoMax} días` },
        ]}
      />

      <Paper variant="lendia_paper" sx={{ mt: 2 }}>
        <CardTitulo
          title="Límites por empresa"
          sub="Cada empresa tiene un monto máximo y un plazo máximo de factura autorizados según su riesgo. Puedes operar con cada una hasta su límite, siempre dentro de tu línea."
        />
        {/* `.fxu-rangos`: chip del color de la empresa (el mismo que en el reparto de la línea), nombre y límite. */}
        <Box
          sx={{
            mt: "12px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
            gap: "8px 20px",
          }}
        >
          {LIMITES_POR_EMPRESA.map((empresa, i) => (
            <Box key={empresa.nombre} sx={{ display: "grid", gridTemplateColumns: "4px 1fr", gap: "10px" }}>
              <Box sx={{ borderRadius: "3px", minHeight: 26, bgcolor: getSegmentColor(theme, i) }} />
              <Box>
                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "text.primary",
                    lineHeight: 1.3,
                    overflowWrap: "anywhere",
                  }}
                >
                  {empresa.nombre}
                </Typography>
                <Typography
                  sx={{ fontSize: 11.5, color: "tokens.textTertiary", fontVariantNumeric: "tabular-nums", mt: "1px" }}
                >
                  Máximo {formatCurrencyWhole(empresa.max)} · plazo hasta {empresa.plazoMax} días
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      <Paper variant="lendia_paper" sx={{ mt: 2 }}>
        <CardTitulo
          title="Cómo se ve una operación"
          sub={`Ejemplo con una factura de ${formatCurrencyWhole(EJEMPLO.factura)} a ${EJEMPLO.dias} días.`}
        />
        <Box sx={{ mt: "14px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
          <Box>
            <Typography sx={STAT_LABEL_SX}>Anticipo ({CONDICIONES.aforo}%)</Typography>
            <Typography sx={STAT_VALUE_SX}>{formatCurrencyWhole(ANTICIPO)}</Typography>
          </Box>
          <Box>
            <Typography sx={STAT_LABEL_SX}>Costo total</Typography>
            <Typography sx={STAT_VALUE_SX}>{formatCurrencyWhole(COSTO)}</Typography>
          </Box>
          <Box>
            <Typography sx={STAT_LABEL_SX}>Recibes hoy</Typography>
            <Typography sx={[STAT_VALUE_SX, { color: "primary.accent" }]}>
              {formatCurrencyWhole(ANTICIPO - COSTO)}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ m: "16px 0 12px", opacity: 0.6 }} />
        <Typography sx={{ fontSize: 13.5, color: "tokens.textTertiary", lineHeight: 1.55 }}>
          El {100 - CONDICIONES.aforo}% restante se te liquida cuando tu cliente paga la factura.
        </Typography>
      </Paper>

      <StepActions
        onNext={handleAccept}
        nextLabel="Aceptar y asignar mi línea"
        nextDisabled={isSubmitting}
        nextTestId="oferta-flux-aceptar-button"
      />
    </Box>
  );
};
